/**
 * BlogPage - Index al articolelor blog e-Tesa
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock as ClockIcon, ArrowRight, Phone, Mail } from "lucide-react";
import { blogArticles, formatArticleDate } from "@/data/blogArticles";

export default function BlogPage() {
  useEffect(() => {
    document.title = "Blog e-Tesa — Articole despre pontaj, devize și management construcții";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Articole, ghiduri și sfaturi pentru firmele de construcții din România: pontaj digital, devize cu AI, e-Factura ANAF, foi de parcurs, gestionare proiecte."
      );
    }
    // Set OG image specific blog
    const ogImg = document.querySelector('meta[property="og:image"]');
    if (ogImg) ogImg.setAttribute("content", "https://e-tesa.ro/og/blog.jpg");
    const twImg = document.querySelector('meta[name="twitter:image"]');
    if (twImg) twImg.setAttribute("content", "https://e-tesa.ro/og/blog.jpg");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", "Blog e-Tesa — Ghiduri pentru construcții");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://e-tesa.ro/blog");
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-blue-50/40 text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-9 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-teal-600 transition-colors">Acasă</Link>
            <Link to="/login" className="px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600 transition-colors">
              Intră în cont
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
          Blog <span className="bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">e-Tesa</span>
        </h1>
        <p className="text-slate-600 text-base sm:text-lg">
          Ghiduri, articole și sfaturi practice pentru firmele de construcții din România.
        </p>
      </section>

      {/* Articles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="group bg-white/70 backdrop-blur rounded-2xl border border-white/80 overflow-hidden hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all"
              data-testid={`blog-card-${article.slug}`}
            >
              <div className="aspect-[16/9] bg-gradient-to-br from-teal-500/10 via-blue-500/10 to-purple-500/10 flex items-center justify-center p-8">
                <img src={article.image} alt={article.title} className="max-h-full opacity-80" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100/70 text-teal-700 rounded-full font-medium">
                    {article.category}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatArticleDate(article.date)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {article.readingTime}
                  </span>
                </div>
                <h2 className="font-semibold text-lg leading-snug mb-2 group-hover:text-teal-700 transition-colors">
                  {article.title}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{article.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-teal-600 group-hover:gap-2 transition-all">
                  Citește articolul
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-10 w-auto mb-3" />
            <p className="text-sm text-slate-400">Platforma all-in-one pentru firmele de construcții din România.</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Contact</h4>
            <a href="tel:0755898253" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-2">
              <Phone className="w-4 h-4" /> 0755 898 253
            </a>
            <a href="mailto:contact@e-tesa.ro" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <Mail className="w-4 h-4" /> contact@e-tesa.ro
            </a>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Legal</h4>
            <Link to="/legal?tab=privacy" className="block text-sm text-slate-400 hover:text-white mb-2">Confidențialitate</Link>
            <Link to="/legal?tab=terms" className="block text-sm text-slate-400 hover:text-white">Termeni</Link>
          </div>
        </div>
        <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} e-Tesa. Toate drepturile rezervate.
        </div>
      </footer>
    </div>
  );
}
